import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle, CheckCircle2, ChevronLeft, FileText, Loader2, MapPin, Printer, Save, Stamp,
  ThumbsDown, ThumbsUp, Undo2,
} from "lucide-react";
import {
  useAdminGetApplicationFormQuery,
  useAdminEndorseApplicationFormMutation,
  useAdminReviewApplicationMutation,
} from "../../api/examsApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

/**
 * The candidate's submitted NIQS application form, as staff read it — laid out
 * like the paper form so it can be checked line by line against the signed copy,
 * and printed.
 *
 * Read-only apart from section 11, the chapter chairman/secretary endorsement,
 * which is completed by hand on paper and recorded here by the secretariat.
 */

const fmtDate = (v?: string | null) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const val = (v: any) => (v === null || v === undefined || v === "" ? "—" : String(v));

function Band({ n, title }: { n: string; title: string }) {
  return (
    <div className="bg-gray-900 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-t-lg">
      {n} {title}
    </div>
  );
}

function Panel({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="break-inside-avoid">
      <Band n={n} title={title} />
      <div className="border border-t-0 rounded-b-lg p-4 bg-white">{children}</div>
    </section>
  );
}

/** Two-column label/value rows, as the form prints them. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-1.5 border-b border-gray-50 last:border-0">
      <div className="sm:w-56 shrink-0 text-[11px] font-bold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="text-sm text-gray-900 break-words">{children}</div>
    </div>
  );
}

/**
 * One figure in a grid: caption above, value below.
 *
 * The counterpart to Field, which lays a fixed-width label BESIDE its value and
 * only works across the full width of the card. In a narrow grid cell there is
 * no room for a label column, so the label goes on its own line and the value
 * gets the whole cell — which is also what lets a long payment reference wrap
 * instead of collapsing to one character per line.
 */
function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 text-sm text-gray-900 break-words">{children}</div>
    </div>
  );
}

function Table({ head, rows, empty }: { head: string[]; rows: React.ReactNode[][]; empty: string }) {
  if (rows.length === 0) return <p className="text-sm text-gray-400">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border">
        <thead>
          <tr className="bg-gray-50 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2 border-b w-10">SN</th>
            {head.map((h) => (
              <th key={h} className="px-3 py-2 border-b">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="px-3 py-2 text-gray-400">{i + 1}</td>
              {cells.map((c, j) => (
                <td key={j} className="px-3 py-2 text-gray-800">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Section 11.0 — the state chapter chairman/secretary endorsement.
 *
 * TEMPORARILY OFF. The Institute is not collecting it on this form at the
 * moment. A flag rather than a deletion because nothing behind it has changed:
 * the form columns, the `endorse` endpoint and the handlers in this component
 * are all still there and still work, so turning it back on is this one word.
 */
const SHOW_CHAPTER_ENDORSEMENT = false;

export default function AdminExamApplicationFormPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useAdminGetApplicationFormQuery(id, { skip: !id });
  const [endorse, { isLoading: endorsing }] = useAdminEndorseApplicationFormMutation();
  const [review, { isLoading: reviewing }] = useAdminReviewApplicationMutation();

  const { can } = useAccess();
  const canDecide = can.perm(PERMS.exam.applicationManage);
  const [decision, setDecision] = useState<"reject" | "return" | null>(null);
  const [reason, setReason] = useState("");

  const [endorsement, setEndorsement] = useState<any>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const form = data?.form;
  const app = data?.application;

  /* The chapter this candidate belongs to — the one whose chairman or secretary
     endorses the application. Stamped onto the application when they apply;
     falls back to the member's own chapter for anything filed before that
     stamp existed. */
  const chapterName: string | null =
    app?.chapter?.name ?? app?.user?.member?.chapter?.name ?? null;

  useEffect(() => {
    if (!form) return;
    setEndorsement({
      chapter_officer_name: form.chapter_officer_name ?? "",
      chapter_officer_membership_no: form.chapter_officer_membership_no ?? "",
      chapter_officer_designation: form.chapter_officer_designation ?? "",
      chapter_officer_phone: form.chapter_officer_phone ?? "",
      chapter_officer_email: form.chapter_officer_email ?? "",
      chapter_endorsed_on: form.chapter_endorsed_on ? String(form.chapter_endorsed_on).slice(0, 10) : "",
    });
  }, [form]);

  const decide = async (action: "approve" | "reject" | "return") => {
    setMsg(null);
    if (action !== "approve" && reason.trim().length < 3) {
      setMsg({ ok: false, text: "Write the candidate a message — it is emailed to them, and it is all they have to act on." });
      return;
    }
    try {
      const res: any = await review({
        appId: id,
        action,
        reason: action === "approve" ? undefined : reason.trim(),
      }).unwrap();
      setDecision(null);
      setReason("");
      setMsg({ ok: true, text: res?.message || "Decision recorded." });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.data?.message || "Could not record the decision." });
    }
  };

  /* Under review, so the secretariat can decide it. `with_committee` is still
     read because rows written by the retired committee leg can be sitting in it
     wherever the migration that moves them has not run — those are applications
     the secretariat is now answerable for, and not recognising the state would
     hide the buttons on exactly the rows the change was meant to free. */
  const inReview = app?.status === "under_review" || app?.status === "with_committee";
  /* An application can be handed back at any point short of approval —
     including while it is still waiting on the chapter. */
  const canReturn = inReview || app?.status === "submitted" || app?.status === "rejected";

  const saveEndorsement = async () => {
    setMsg(null);
    try {
      await endorse({ appId: id, body: endorsement }).unwrap();
      setMsg({ ok: true, text: "Chapter endorsement recorded." });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.data?.message || "Could not record the endorsement." });
    }
  };

  if (isLoading) {
    return (
      <div className="py-24 flex justify-center text-gray-300">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-rose-600">
          Could not load this application form.
        </div>
      </div>
    );
  }

  const input =
    "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-900/10";
  const label = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      {/* Heading */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
            <FileText className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900">
              {app?.exam?.code ? `${app.exam.code} — ` : ""}Application form
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {app?.user?.name ?? "—"}
              {app?.user?.member?.membership_no ? ` · ${app.user.member.membership_no}` : ""}
              {app?.diet?.title ? ` · ${app.diet.title}` : ""}
              {app?.exam_no ? ` · ${app.exam_no}` : ""}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {/* Whose chapter this candidate belongs to — violet, so it reads as a
              different KIND of fact from the form's completeness badges beside
              it. It is the chapter that endorses, so a reviewer needs to see it
              without hunting through the grid below. */}
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-violet-50 text-violet-700 border-violet-200">
            <MapPin className="w-3 h-3" />
            {chapterName ? `${chapterName} chapter` : "No chapter on record"}
          </span>

          {!data.required ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-gray-50 text-gray-500 border-gray-200">
              This exam does not require the form
            </span>
          ) : !data.started ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3" /> Not started by the candidate
            </span>
          ) : data.complete ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3" /> Incomplete
            </span>
          )}

          {form?.declaration_accepted && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
              Declared {fmtDate(form.declared_at)}
            </span>
          )}
          {form?.chapter_endorsed_on && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-indigo-50 text-indigo-700 border-indigo-200">
              <Stamp className="w-3 h-3" /> Endorsed {fmtDate(form.chapter_endorsed_on)}
            </span>
          )}
        </div>

        {Array.isArray(data.missing) && data.missing.length > 0 && (
          <ul className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 list-disc list-inside space-y-0.5">
            {data.missing.map((m: string) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </div>

      {/* The application itself — everything the list's row could only hint at.
          Shown for every exam, including those with no form to read. */}
      <div className="bg-white border rounded-xl p-5">
        <h2 className="font-semibold text-gray-900">Application</h2>

        {/* Stat, not Field. Field is the full-width row used by the sections
            below — a fixed 224px label beside its value — and inside a
            four-column grid that label is wider than the whole cell. Being
            `shrink-0` it refused to give way, so the value was crushed to a
            column one character wide and the labels ran over each other. */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Status">{val(app?.status).replace(/_/g, " ")}</Stat>
          <Stat label="Chapter">
            <span className="text-violet-700 font-semibold">
              {val(chapterName)}
            </span>
          </Stat>
          <Stat label="Exam number">{val(app?.exam_no)}</Stat>
          <Stat label="Centre">
            {app?.center?.name
              ? `${app.center.name}${app.center.state ? ` (${app.center.state})` : ""}`
              : "—"}
          </Stat>
          <Stat label="Submitted">{fmtDate(app?.submitted_at ?? app?.created_at)}</Stat>
          <Stat label="Fee">
            {app?.amount != null ? `₦${Number(app.amount / 100).toLocaleString()}` : "—"}
            {app?.paid_at ? ` · paid ${fmtDate(app.paid_at)}` : ""}
          </Stat>
          <Stat label="Result">
            {app?.result?.overall_status
              ? String(app.result.overall_status).replace(/_/g, " ")
              : "Not graded"}
          </Stat>
          <Stat label="Payment reference">
            <span className="font-mono text-xs break-all">{val(app?.reference)}</span>
          </Stat>
          <Stat label="Diet">{val(app?.diet?.title)}</Stat>
        </div>

        {Array.isArray(app?.papers) && app.papers.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-400 mb-1.5">Papers</p>
            <div className="flex flex-wrap gap-1.5">
              {app.papers.map((p: any) => (
                <span
                  key={p.id}
                  title={p.paper?.title}
                  className="px-2 py-1 rounded-lg border bg-gray-50 text-xs text-gray-700"
                >
                  {p.paper?.code ?? "Paper"}
                  {p.status ? ` · ${p.status}` : ""}
                  {p.score != null ? ` · ${p.score}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(app?.registered_courses) && app.registered_courses.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-400 mb-1.5">Registered courses</p>
            <div className="flex flex-wrap gap-1.5">
              {app.registered_courses.map((c: any) => (
                <span
                  key={c.id}
                  title={c.course?.name}
                  className="px-2 py-1 rounded-lg border bg-gray-50 text-xs text-gray-700"
                >
                  {c.course?.code ?? c.course?.name ?? "Course"}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* The fee, near the top. A reviewer's first question about any
          application is whether it was paid for, and the page could only say
          so indirectly — not what to quote back to a candidate who insists
          they have. */}
      <ExamFeeReceipt receipt={data?.fee_payment} />

      {/* The chapter's endorsement — what puts an application in front of the
          secretariat at all. Shown whenever it is in, so a reviewer can read
          what the chapter said before deciding anything. */}
      {app?.chapter_endorsed_at && (
        <div className="bg-white border rounded-xl p-5">
          <p className="text-sm font-semibold text-gray-900">
            Endorsed by {chapterName ? `the ${chapterName} chapter` : "the candidate's chapter"}
            {app?.chapter_endorsed_by?.name ? ` · ${app.chapter_endorsed_by.name}` : ""} · {fmtDate(app.chapter_endorsed_at)}
          </p>
          {app?.chapter_endorse_comment && (
            <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{app.chapter_endorse_comment}</p>
          )}
        </div>
      )}

      {/* The secretariat's part, which is smaller than it used to be: hand the
          application to the Membership Committee, or hand it back to the
          candidate. Approving and rejecting are the committee's — see below.
          The route grants exam.access, which reads the form; acting on it is
          exam.application.manage. */}
      {canReturn && canDecide && (
        <div className="bg-white border-2 border-sky-200 rounded-xl p-5 print:hidden">
          <h2 className="font-semibold text-gray-900">
            {app?.status === "rejected" ? "Turned down" : "Review this application"}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {app?.status === "rejected"
              ? "Leave it as it stands, or hand it back to the candidate — corrected, it comes back to you."
              : app?.status === "submitted"
                ? "This one is still waiting on the candidate's chapter. You can hand it back to them now if something is wrong with it; corrected, it comes back here for the chapter."
                : "Approve it — which is the end of the road for an exam application — turn it down, or hand it back to the candidate with a note. A returned application keeps the chapter's endorsement and comes straight back to you."}
          </p>

          {decision && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {decision === "reject"
                  ? "Why is this application being turned down? *"
                  : "What should the candidate correct? *"}
              </label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  decision === "reject"
                    ? "Say why it cannot be accepted…"
                    : "Tell them what to correct…"
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
              />
              <p className="mt-1.5 text-xs text-gray-500">
                {decision === "reject" ? (
                  <>
                    Shown to {app?.user?.name ?? "the candidate"} on their application, and the only
                    record of why. If the fault is something they could fix, return it instead —
                    a rejection can still be handed back, but it reads as final to them.
                  </>
                ) : (
                  <>
                    This message is emailed to {app?.user?.name ?? "the candidate"}
                    {app?.user?.email ? ` (${app.user.email})` : ""} and shown on their application.
                    It is the only thing they have to act on, so say plainly what is wrong.
                  </>
                )}
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {decision === null ? (
              <>
                {/* Approve and reject are the secretariat's again as of
                    2026-09-13. Offered only while the application is actually
                    under review — from `submitted` the chapter still owes an
                    endorsement, and deciding over its head is the shortcut this
                    ladder exists to prevent. The server refuses it too. */}
                {inReview && (
                  <>
                    <button
                      onClick={() => decide("approve")}
                      disabled={reviewing}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stamp className="w-4 h-4" />}
                      Approve application
                    </button>
                    <button
                      onClick={() => setDecision("reject")}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-medium hover:bg-rose-50"
                    >
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => setDecision("return")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-200 text-amber-700 text-sm font-medium hover:bg-amber-50"
                >
                  <Undo2 className="w-4 h-4" /> Return to candidate
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => decide(decision)}
                  disabled={reviewing}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                    decision === "reject"
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  }`}
                >
                  {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {decision === "reject" ? "Confirm rejection" : "Confirm return"}
                </button>
                <button
                  onClick={() => { setDecision(null); setReason(""); }}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {msg && (
            <div
              className={`mt-4 px-3 py-2 rounded-lg text-xs border ${
                msg.ok
                  ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                  : "bg-rose-50 border-rose-100 text-rose-700"
              }`}
            >
              {msg.text}
            </div>
          )}
        </div>
      )}

      {/* A decision already made — show it with its reason */}
      {(app?.review_note || form?.review_note) && !inReview && (
        <div className="bg-white border rounded-xl p-5">
          <p className="text-sm font-semibold text-gray-900">
            {app?.status === "rejected" ? "Rejected" : "Returned to the candidate"}
          </p>
          <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{app?.review_note ?? form?.review_note}</p>
        </div>
      )}

      {/* Nothing declared yet, so what follows is the Institute's own record of
          this member — not their word. Say so before any of it is read. */}
      {!data.started && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">The candidate has not started this form.</p>
            <p className="mt-0.5">
              Everything below is drawn from their member record and probationer application —
              what the Institute already holds. It has not been declared, signed or endorsed by
              them, and must not be reviewed as if it had.
            </p>
          </div>
        </div>
      )}

      {!data.started && !form ? (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-500">
          We hold no record for this member to show.
        </div>
      ) : (
        <>
          <Panel n="1.0" title="Personal details">
            <Field label="NIQS membership number">{val(form.membership_no)}</Field>
            <Field label="Surname">{val(form.surname)}</Field>
            <Field label="Other names">{val(form.other_names)}</Field>
            <Field label="Title / gender">{`${val(form.title)} / ${val(form.gender)}`}</Field>
            <Field label="Date of birth">{fmtDate(form.date_of_birth)}</Field>
            <Field label="Chapter">{val(form.chapter)}</Field>
            <Field label="Phone number">{val(form.phone)}</Field>
            <Field label="Email address">{val(form.email)}</Field>
            <Field label="Postal address">{val(form.postal_address)}</Field>
            <Field label="Current membership grade">{val(form.membership_grade)}</Field>
            <Field label="Date of registration">{fmtDate(form.date_of_registration)}</Field>
            <Field label="Diet">{val(form.diet)}</Field>
            <Field label="Disability">
              {form.disability ? val(form.disability_details) || "Yes" : "No"}
            </Field>
          </Panel>

          <Panel n="2.0" title="Preferred interview centre">
            <p className="text-sm text-gray-900">
              {val(form.preferred_centre ?? app?.center?.name)}
            </p>
          </Panel>

          <Panel n="3.0" title={`O'level subjects result (${data.required_olevel_subjects} credits)`}>
            <Table
              head={["Subject", "Grade", "Exam", "Year"]}
              empty="No O'level results entered."
              rows={(data.olevels ?? []).map((o: any) => [
                val(o.subject),
                <span className="font-semibold">{val(o.grade)}</span>,
                (o.exam_type ?? "—").toString().toUpperCase(),
                val(o.exam_year),
              ])}
            />
          </Panel>

          <Panel n="4.0" title="Education">
            <Table
              head={["University / institution", "Country", "Degree / diploma", "Completed"]}
              empty="No institutions entered."
              rows={(data.educations ?? []).map((e: any) => [
                val(e.institution),
                val(e.country),
                val(e.qualification),
                fmtDate(e.completed_on),
              ])}
            />
          </Panel>

          <Panel n="5.0" title="Membership of other professional institutions">
            <Table
              head={["Institution", "Current grade", "How achieved", "Year"]}
              empty="None declared."
              rows={(data.memberships ?? []).map((m: any) => [
                val(m.institution),
                val(m.current_grade),
                val(m.how_achieved),
                val(m.year_obtained),
              ])}
            />
          </Panel>

          <Panel n="6.0" title="Examinations and logbook stages passed">
            {/* Held by NIQS — not the candidate's word, so it is shown as ours. */}
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
              From the NIQS record
            </p>
            <Table
              head={["Record", "Detail", "Reference", "Released"]}
              empty="No released logbook or exam results on this member's record."
              rows={[
                ...(data.system_record?.logbooks ?? []).map((lb: any) => [
                  `Logbook Stage ${val(lb.stage)}`,
                  val(lb.final_grade),
                  lb.total_score != null ? `score ${lb.total_score}` : "—",
                  val(lb.passed_on),
                ]),
                ...(data.system_record?.exams ?? []).map((x: any) => [
                  val(x.exam_title ?? x.exam_code),
                  val(x.diet),
                  val(x.exam_no),
                  val(x.passed_on),
                ]),
              ]}
            />

            {/* Declared by the candidate — NIQS cannot verify these itself. */}
            <p className="mt-4 mb-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
              Declared — passed elsewhere
            </p>
            <Table
              head={["Examination", "Awarding body", "Year", "Exam no. / result"]}
              empty="None declared."
              rows={(data.other_exams ?? []).map((x: any) => [
                val(x.exam_name),
                val(x.awarding_body),
                val(x.exam_year),
                [x.exam_number, x.result].filter(Boolean).join(" · ") || "—",
              ])}
            />

            {(form.tpc_exam_no || form.logbook_stage3_exam_no || form.rics_membership_no) && (
              <div className="mt-4">
                <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Declared on an earlier version of this form
                </p>
                <Table
                  head={["Details", "Year / month", "Exam no.", "Centre"]}
                  empty="—"
                  rows={[
                    ["TPC examination", val(form.tpc_exam_year_month), val(form.tpc_exam_no), val(form.tpc_exam_centre)],
                    [
                      "Logbook/diary stage 3",
                      val(form.logbook_stage3_year_month),
                      val(form.logbook_stage3_exam_no),
                      val(form.logbook_stage3_centre),
                    ],
                  ]}
                />
                {form.rics_membership_no && (
                  <div className="mt-3">
                    <Field label="RICS membership">{val(form.rics_membership_no)}</Field>
                  </div>
                )}
              </div>
            )}
          </Panel>

          <Panel n="7.0" title="Professional experience">
            <Table
              head={["Employer", "Position held & dates", "Duties"]}
              empty="No experience entered."
              rows={(data.experiences ?? []).map((e: any) => [
                <>
                  <div className="font-medium">{val(e.employer_name)}</div>
                  {e.employer_address && (
                    <div className="text-xs text-gray-400">{e.employer_address}</div>
                  )}
                </>,
                `${val(e.position)} (${fmtDate(e.start_date)} – ${e.end_date ? fmtDate(e.end_date) : "Date"})`,
                val(e.duties),
              ])}
            />
          </Panel>

          <Panel n="8.0" title="Employer details">
            {data.employer ? (
              <>
                <Field label="Name of employer">{val(data.employer.name)}</Field>
                <Field label="Business address">{val(data.employer.business_address)}</Field>
                <Field label="Nature of business">{val(data.employer.nature_of_business)}</Field>
                <Field label="Position of applicant">{val(data.employer.applicant_position)}</Field>
                <Field label="Date of employment">{fmtDate(data.employer.employment_date)}</Field>
                <Field label="Attested">
                  {data.employer.attested ? `Yes — ${fmtDate(data.employer.signed_on)}` : "Signed on the paper form"}
                </Field>
              </>
            ) : (
              <p className="text-sm text-gray-400">Not provided.</p>
            )}
          </Panel>

          <Panel n="9.0" title="Sponsor details">
            {data.sponsor ? (
              <>
                <Field label="Sponsor's membership number">
                  {val(data.sponsor.membership_no)}
                  {data.sponsor.member ? (
                    <span className="ml-2 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      Matched{data.sponsor.member.grade ? ` · ${data.sponsor.member.grade}` : ""}
                    </span>
                  ) : (
                    <span className="ml-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      No matching member
                    </span>
                  )}
                </Field>
                <Field label="Full name">{val(data.sponsor.full_name)}</Field>
                <Field label="Email address">{val(data.sponsor.email)}</Field>
                <Field label="Contact address">{val(data.sponsor.contact_address)}</Field>
                <Field label="Membership grade">{val(data.sponsor.membership_grade)}</Field>
                <Field label="Registration date">{fmtDate(data.sponsor.registration_date)}</Field>
              </>
            ) : (
              <p className="text-sm text-gray-400">Not provided.</p>
            )}
          </Panel>

          <Panel n="10.0" title="Applicant's declaration">
            <p className="text-sm text-gray-700">
              {form.declaration_accepted
                ? `Declared by the applicant on ${fmtDate(form.declared_at)}.`
                : "The applicant has not accepted the declaration."}
            </p>
          </Panel>

          {/* 11.0 — the one editable section. There is nothing to endorse until
              the candidate has actually filled and signed a form; the endpoint
              rejects it too, so the inputs would only lead to a 422.
              Currently hidden — see SHOW_CHAPTER_ENDORSEMENT. */}
          {SHOW_CHAPTER_ENDORSEMENT && (
          <section className="break-inside-avoid">
            <Band n="11.0" title="State chapter chairman/secretary (compulsory)" />
            <div className="border border-t-0 rounded-b-lg p-4 bg-white">
              {!data.started ? (
                <p className="text-sm text-gray-500">
                  Available once the candidate has filled and submitted their form.
                </p>
              ) : (
              <>
              <p className="text-xs text-gray-500 mb-4">
                Completed by hand on the signed form — record it here.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 print:hidden">
                <div>
                  <label className={label}>Name *</label>
                  <input
                    className={input}
                    value={endorsement.chapter_officer_name ?? ""}
                    onChange={(e) => setEndorsement({ ...endorsement, chapter_officer_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className={label}>NIQS membership number</label>
                  <input
                    className={input}
                    value={endorsement.chapter_officer_membership_no ?? ""}
                    onChange={(e) =>
                      setEndorsement({ ...endorsement, chapter_officer_membership_no: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={label}>Designation</label>
                  <input
                    className={input}
                    value={endorsement.chapter_officer_designation ?? ""}
                    onChange={(e) =>
                      setEndorsement({ ...endorsement, chapter_officer_designation: e.target.value })
                    }
                    placeholder="Chairman / Secretary"
                  />
                </div>
                <div>
                  <label className={label}>Phone number</label>
                  <input
                    className={input}
                    value={endorsement.chapter_officer_phone ?? ""}
                    onChange={(e) => setEndorsement({ ...endorsement, chapter_officer_phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className={label}>Email</label>
                  <input
                    type="email"
                    className={input}
                    value={endorsement.chapter_officer_email ?? ""}
                    onChange={(e) => setEndorsement({ ...endorsement, chapter_officer_email: e.target.value })}
                  />
                </div>
                <div>
                  <label className={label}>Date signed</label>
                  <input
                    type="date"
                    className={input}
                    value={endorsement.chapter_endorsed_on ?? ""}
                    onChange={(e) => setEndorsement({ ...endorsement, chapter_endorsed_on: e.target.value })}
                  />
                </div>
              </div>

              {/* Printed copy shows the recorded values, not the inputs. */}
              <div className="hidden print:block">
                <Field label="Name">{val(form.chapter_officer_name)}</Field>
                <Field label="NIQS membership number">{val(form.chapter_officer_membership_no)}</Field>
                <Field label="Designation">{val(form.chapter_officer_designation)}</Field>
                <Field label="Phone number">{val(form.chapter_officer_phone)}</Field>
                <Field label="Email">{val(form.chapter_officer_email)}</Field>
                <Field label="Date">{fmtDate(form.chapter_endorsed_on)}</Field>
              </div>

              {msg && (
                <div
                  className={`mt-4 px-3 py-2 rounded-lg text-xs border print:hidden ${
                    msg.ok
                      ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                      : "bg-rose-50 border-rose-100 text-rose-700"
                  }`}
                >
                  {msg.text}
                </div>
              )}

              <div className={`mt-4 justify-end print:hidden ${canDecide ? "flex" : "hidden"}`}>
                <button
                  onClick={saveEndorsement}
                  disabled={endorsing || !endorsement.chapter_officer_name}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
                >
                  {endorsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Record endorsement
                </button>
              </div>
              </>
              )}
            </div>
          </section>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The exam fee receipt.
 *
 * Read off the APPLICATION, not the candidate's account: an exam fee is raised
 * against this registration — initiatePayment writes the invoice, the payment
 * and the reference onto the row — unlike the entry-application fee, which
 * belongs to the applicant's signup.
 *
 * Shows what a reviewer would check against a bank statement or quote back to
 * a candidate: reference, amount, date and how it was taken.
 */
function ExamFeeReceipt({ receipt }: { receipt: any }) {
  // No fee was ever raised. A real state, not a failure: a draft has none yet,
  // and an application carried into a diet as a deferral never had one — the
  // candidate paid for the sitting they were carried from. Saying "unpaid"
  // would accuse them of something the record does not show.
  if (!receipt) {
    return (
      <div className="bg-white border rounded-xl p-4 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Exam fee</span> — no payment has been
        started for this application.
      </div>
    );
  }

  const paid = !!receipt.settled;
  const money = (v: any) =>
    v === null || v === undefined
      ? "—"
      : `${receipt.currency ?? "NGN"} ${Number(v).toLocaleString("en-NG", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  return (
    <div
      className={`rounded-xl border p-5 ${
        paid ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p
            className={`text-[10px] font-black uppercase tracking-widest ${
              paid ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            Exam fee
          </p>
          <p className={`text-lg font-bold ${paid ? "text-emerald-900" : "text-amber-900"}`}>
            {paid ? "Paid" : "Awaiting payment"}
            {paid && receipt.recorded_offline ? " · recorded by hand" : ""}
          </p>
        </div>
        <p className={`text-2xl font-black ${paid ? "text-emerald-800" : "text-amber-800"}`}>
          {money(paid ? receipt.amount_paid ?? receipt.amount : receipt.amount)}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Row label="Receipt no." value={receipt.receipt_number} />
        <Row label="Reference" value={receipt.reference} />
        <Row label="Paid on" value={fmtDate(receipt.paid_at)} />
        <Row label="Method" value={receipt.method ?? receipt.gateway} />
        <Row label="Invoice no." value={receipt.invoice_number} />
        <Row label="Invoice status" value={receipt.invoice_status} />
        {Number(receipt.balance) > 0 && (
          <Row label="Outstanding" value={money(receipt.balance)} />
        )}
      </div>

      {paid && receipt.recorded_offline && (
        <p className="mt-3 text-[11px] text-emerald-800">
          Settled without a gateway payment — an offline or bank transfer recorded by the
          secretariat.
        </p>
      )}
    </div>
  );
}

/** One labelled value in the receipt grid. */
function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">
        {value === null || value === undefined || value === "" ? "—" : value}
      </div>
    </div>
  );
}
